// components/notifications/NotificationBell.tsx
'use client';

import { memo, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { Bell } from '@/components/ui/lucide-memo';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { LEASING_EVENT_NAME } from '#lib/socket-events';
import { notificationToastId, selectVisibleNotifications } from '@/components/notifications/notificationSelectors';
import type { KernelEntityRuntimeState, NotificationDTO } from '#types/types';
import styles from '@/components/portal/Notice.module.css';

const extractNotificationEvents = (payload: unknown): Array<{
  entityType?: unknown;
  entityId?: unknown;
  eventType?: unknown;
  payload?: unknown;
}> => {
  if (Array.isArray(payload)) return payload.filter(event => event && typeof event === 'object');
  if (payload && typeof payload === 'object') return [payload as { entityType?: unknown; eventType?: unknown; payload?: unknown }];
  return [];
};

function NotificationBell() {
  const { kernel } = useKernel();
  const userId = useKernelStore((state) => state.userContext.dbUserId ?? null);
  const notifications = useKernelStore(selectVisibleNotifications);

  const unreadCount = notifications.length;

  useEffect(() => {
    if (!userId) return;
    void kernel.current?.refreshInventory('Notification', { includeArchived: false, unreadOnly: true }, 50, { force: true, throwOnError: true }).catch(() => {
      if (kernel.current?.getUserContext().dbUserId === userId) kernel.current.handleError('Could not load notifications.');
    });
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const shownToastIds = new Set<string>();
    const unsubscribe = kernel.current?.subscribeToSocketEvent(LEASING_EVENT_NAME, (payload: unknown) => {
      const k = kernel.current;
      if (!k || k.getUserContext().dbUserId !== userId) return;
      const events = extractNotificationEvents(payload);
      events.forEach((event) => {
        if (event.entityType !== 'Notification') return;
        const notification = event.payload as Partial<NotificationDTO> | undefined;
        const id = typeof notification?.id === 'string' ? notification.id : event.entityId;
        if (typeof id !== 'string' || !id) return;
        const stored = k.getState().inventory.Notification.registry[id];
        if ((notification?.userId ?? stored?.userId) !== userId) return;
        const toastId = notificationToastId(userId, id);
        if (event.eventType === 'DELETE' || notification?.readAt || notification?.archivedAt || stored?.readAt || stored?.archivedAt) {
          toast.dismiss(toastId);
          return;
        }
        if (event.eventType !== 'CREATE' || !notification) return;
        const runtime = k.readEntityKernelState('Notification', id)?.runtime as KernelEntityRuntimeState<'Notification'> | undefined;
        if (runtime?.toastShownForUserId === userId) return;
        k.patchEntityRuntimeState('Notification', id, { toastShownForUserId: userId });
        shownToastIds.add(toastId);
        const title = typeof notification.title === 'string' && notification.title ? notification.title : 'Notification';
        const body = typeof notification.body === 'string' ? notification.body : undefined;
        // Bottom left, where the eye rests after an action (owner, 2026-10-05), in the menus' grey;
        // closing it is reading it.
        toast(title, {
          id: toastId, description: body, position: 'bottom-left', duration: 10_000, closeButton: true,
          style: { background: '#353535', color: '#ECECEC', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, fontSize: 14 },
          classNames: { title: styles.toastTitle, description: styles.toastDescription },
          onDismiss: () => {
            if (kernel.current?.getUserContext().dbUserId !== userId) return;
            void kernel.current?.updateEntity('Notification', id, { readAt: new Date().toISOString() }).catch(() => undefined);
          },
        });
      });
    });
    return () => {
      unsubscribe?.();
      shownToastIds.forEach(id => toast.dismiss(id));
    };
  }, [userId]);

  const handleOpen = useCallback(() => {
    kernel.current?.openModal('NotificationCenter');
  }, [kernel]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleOpen}
        className={styles.bell}
        aria-label="Notifications"
        aria-haspopup="dialog"
      >
        <Bell size={18} strokeWidth={1.7} />
        {unreadCount > 0 ? (
          <span className={styles.badge}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </button>
    </div>
  );
}

export default memo(NotificationBell);
